import type { DefaultTheme } from 'styled-components';
import styled, { keyframes } from 'styled-components';

import { FlexRowCenterVertical } from '@main/core-ui';
import { sp } from '@main/theme';
import { hexToRgb } from '@main/theme-types';

interface RadioInputContainerProps {
  /** flag for whether radio input is disabled */
  disabled: boolean;
  /** current checked state of radio input */
  radioInputValue: 'checked' | 'unchecked';
  /** styled-components theme set up by a theme provider in each app */
  theme: DefaultTheme;
}

const hexToHalfOpacity = (hexColor: string): string => {
  const { r, g, b } = hexToRgb(hexColor);
  return `rgba(${r}, ${g}, ${b}, 0.5)`;
};

/**
 * ui state of the radio input div
 */
type RadioInputUIState = 'hover' | 'active' | 'disabled';
/**
 * utility function to get a mapping of ui states to colors from a given theme
 */
type GetColorsMap = (theme: DefaultTheme) => {
  [key in RadioInputUIState]: { [key in 'checked' | 'unchecked']: string };
};
const getBorderColorsMap: GetColorsMap = ({ colors }: DefaultTheme) => ({
  hover: {
    unchecked: colors.primary,
    checked: colors.primary,
  },
  active: {
    unchecked: colors.primary,
    checked: colors.primary,
  },
  disabled: {
    unchecked: hexToHalfOpacity(colors.primary),
    checked: hexToHalfOpacity(colors.primary),
  },
});
const getBackgroundColorsMap: GetColorsMap = ({ colors }: DefaultTheme) => ({
  hover: {
    unchecked: colors.white,
    checked: colors.darkPrimary,
  },
  active: {
    unchecked: colors.white,
    checked: colors.primary,
  },
  disabled: {
    unchecked: colors.white,
    checked: hexToHalfOpacity(colors.primary),
  },
});
const getColor =
  (colorsMapGetter: GetColorsMap) =>
  (radioInputUIState: RadioInputUIState) =>
  ({ disabled, radioInputValue, theme }: RadioInputContainerProps) =>
    colorsMapGetter(theme)[disabled ? 'disabled' : radioInputUIState][
      radioInputValue
    ];
const getBorderColor = getColor(getBorderColorsMap);
const getBackgroundColor = getColor(getBackgroundColorsMap);

export const HiddenRadio = styled.input`
  position: absolute;
  width: 0;
  opacity: 0;

  // color for label when radio input is checked
  &:checked + label {
    color: ${({ theme }) => getBorderColorsMap(theme).active.checked};
  }

  // color for radio input circle when radio input is checked
  &:checked + label > div {
    background-size: 100%;
    border-color: ${({ theme }) => getBorderColorsMap(theme).active.checked};
    background: ${({ theme }) => getBackgroundColorsMap(theme).active.checked};
  }

  // color for label when radio input is disabled and checked
  &:checked :disabled + label {
    color: ${({ theme }) => getBackgroundColorsMap(theme).disabled.checked};
  }

  // color for radio input circle when radio input is disabled and checked
  &:checked :disabled + label > div {
    background-size: 100%;
    border-color: ${({ theme }) => getBorderColorsMap(theme).disabled.checked};
    background: ${({ theme }) =>
      getBackgroundColorsMap(theme).disabled.checked};
  }

  // properties for label when radio input is disabled
  &:disabled ~ label {
    color: ${({ theme }) => theme.colors.textLightOnBg};
    cursor: not-allowed;
  }

  // color for radio input circle when radio input is disabled
  &:disabled + label > div {
    border-color: ${({ theme }) =>
      getBorderColorsMap(theme).disabled.unchecked};
    cursor: not-allowed;
  }
`;

export const RadioInputCircle = styled.div`
  position: relative;
  height: 16px;
  width: 16px;
  min-height: 16px;
  min-width: 16px;
  border-radius: 50%;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-width: 1px;
  border-style: solid;
  background-size: 0;
  transition: all 200ms ease-out;
  border-color: ${({ theme }) => theme.colors.gray3};
`;

export const RadioInputContainer = styled(
  FlexRowCenterVertical,
)<RadioInputContainerProps>`
  position: relative;
  border-radius: ${sp(1)};
  border-width: 1px;
  border-style: solid;
  padding: 6px;
  border-color: ${({ radioInputValue, theme }) =>
    radioInputValue === 'checked' ? theme.colors.primary : theme.colors.gray3};

  &:hover > input[type='radio'] :not(:disabled) + label {
    color: ${getBorderColor('hover')};
  }

  &:hover > input[type='radio'] :not(:disabled) + label > div {
    border-color: ${getBorderColor('hover')};
    background: ${getBackgroundColor('hover')};
  }

  &:active > input[type='radio'] :not(:disabled) + label {
    color: ${getBorderColor('active')};
  }

  &:active > input[type='radio'] :not(:disabled) + label > div {
    transform: scale(90%);
    transition: transform 0ms;
    border-color: ${getBorderColor('active')};
    background: ${getBackgroundColor('active')};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const expand = keyframes`
  from {
    transform: scale(0);
  }

  to {
    transform: scale(1);
  }
`;

export const CenterDot = styled.div`
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background: white;
  animation: 200ms ${expand} ease-out;
`;

export const LabelWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: ${sp(1)};
`;
